/**
 * CSV Download Utility
 *
 * @description Triggers browser download of CSV file with proper encoding
 * @version 1.0.0
 * @created 2025-10-27
 * @author eCompanion2 CSV Export Feature
 *
 * Purpose: Provide browser-compatible CSV download functionality
 * with UTF-8 BOM for Excel compatibility.
 *
 * Features:
 * - UTF-8 BOM encoding (Excel-friendly)
 * - Automatic cleanup of download link
 * - Cross-browser compatible
 * - Memory-efficient (uses Blob API)
 */

/**
 * Download CSV file in browser
 *
 * Creates a temporary download link, triggers download, and cleans up resources.
 * The CSV content should already include UTF-8 BOM if needed for Excel compatibility.
 *
 * @param csvContent - Complete CSV file content (including BOM if needed)
 * @param filename - Desired filename (e.g., "kalkulacja_taryfy_2025_20251027_143052.csv")
 *
 * @example
 * const csv = generateCalculationCSV(calculationData);
 * downloadCSV(csv, "kalkulacja_taryfy_2025_20251027_143052.csv");
 * // Browser triggers download of CSV file
 */
export function downloadCSV(csvContent: string, filename: string): void {
  // Validate inputs
  if (!csvContent) {
    throw new Error('CSV content is required');
  }

  if (!filename) {
    throw new Error('Filename is required');
  }

  // Ensure filename has .csv extension
  if (!filename.endsWith('.csv')) {
    filename = filename + '.csv';
  }

  // Create Blob with UTF-8 encoding
  // Note: BOM should already be included in csvContent
  const blob = new Blob([csvContent], {
    type: 'text/csv;charset=utf-8;'
  });

  // Create download link
  const link = document.createElement('a');

  // Create object URL for Blob
  const url = URL.createObjectURL(blob);
  link.href = url;
  link.download = filename;

  // Hide link (not visible to user)
  link.style.display = 'none';

  // Append to document (required for Firefox)
  document.body.appendChild(link);

  // Trigger download
  link.click();

  // Cleanup: Remove link and revoke object URL
  // Use setTimeout to ensure download starts before cleanup
  setTimeout(() => {
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, 100);
}

/**
 * Generate filename for calculation CSV export
 *
 * Format: kalkulacja_taryfy_YYYY_YYYYMMDD_HHMMSS.csv
 * - First YYYY: calculation year
 * - Second part: generation timestamp
 *
 * @param calculationYear - Year of calculation
 * @returns Formatted filename
 *
 * @example
 * const filename = generateCalculationFilename(2025);
 * // Returns: "kalkulacja_taryfy_2025_20251027_143052.csv"
 */
export function generateCalculationFilename(calculationYear: number): string {
  const now = new Date();

  // Format date as YYYYMMDD
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  const dateStr = `${year}${month}${day}`;

  // Format time as HHMMSS
  const hours = String(now.getHours()).padStart(2, '0');
  const minutes = String(now.getMinutes()).padStart(2, '0');
  const seconds = String(now.getSeconds()).padStart(2, '0');
  const timeStr = `${hours}${minutes}${seconds}`;

  // Build filename
  return `kalkulacja_taryfy_${calculationYear}_${dateStr}_${timeStr}.csv`;
}

/**
 * Download calculation CSV (convenience function)
 *
 * Combines CSV generation and download with automatic filename.
 *
 * @param csvContent - Complete CSV content
 * @param calculationYear - Year of calculation
 *
 * @example
 * const csv = generateCalculationCSV(calculationData);
 * downloadCalculationCSV(csv, 2025);
 * // Downloads: kalkulacja_taryfy_2025_20251027_143052.csv
 */
export function downloadCalculationCSV(csvContent: string, calculationYear: number): void {
  const filename = generateCalculationFilename(calculationYear);
  downloadCSV(csvContent, filename);
}

/**
 * Check if browser supports download functionality
 *
 * @returns True if download is supported, false otherwise
 */
export function isBrowserDownloadSupported(): boolean {
  // Check if we're in a browser environment
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return false;
  }

  // Check if Blob API is available
  if (typeof Blob === 'undefined') {
    return false;
  }

  // Check if URL.createObjectURL is available
  if (typeof URL === 'undefined' || typeof URL.createObjectURL === 'undefined') {
    return false;
  }

  return true;
}

/**
 * Default export
 */
export default {
  downloadCSV,
  generateCalculationFilename,
  downloadCalculationCSV,
  isBrowserDownloadSupported
};
